/**
 * "Never search these sites" / "Only search these sites" — the agent's web
 * search site lists, shown under the web search toggle while it is on.
 *
 * The rules (what a site is, the cap, the both-lists rule) live in
 * ../utils/webSearchSites.ts and mirror the server, which validates every save.
 */
import { useState } from 'react';
import { useLanguage } from '@hooks/useLanguage';
import {
  MAX_SITES_PER_LIST,
  addSite,
  effectiveAllowed,
  type SiteError,
  type WebSearchSites,
} from '../utils/webSearchSites';
import './WebSearchSitesEditor.css';

interface Props {
  value: WebSearchSites;
  onChange: (next: WebSearchSites) => void;
  disabled?: boolean;
}

type ListKey = keyof WebSearchSites;

export function WebSearchSitesEditor({ value, onChange, disabled }: Props) {
  const { t } = useLanguage();
  const both = value.blocked_domains.length > 0 && value.allowed_domains.length > 0;

  return (
    <div className="ws-sites" data-testid="web-search-sites">
      <SiteList
        listKey="blocked_domains"
        label={t('bots.modal.webSearchSites.blockedLabel')}
        help={t('bots.modal.webSearchSites.blockedHelp')}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      <SiteList
        listKey="allowed_domains"
        label={t('bots.modal.webSearchSites.allowedLabel')}
        help={t('bots.modal.webSearchSites.allowedHelp')}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {both && (
        <p className="ws-sites__rule" data-testid="web-search-sites-rule">
          {t('bots.modal.webSearchSites.bothRule', { count: effectiveAllowed(value).length })}
        </p>
      )}
    </div>
  );
}

function SiteList({
  listKey, label, help, value, onChange, disabled,
}: {
  listKey: ListKey;
  label: string;
  help: string;
  value: WebSearchSites;
  onChange: (next: WebSearchSites) => void;
  disabled?: boolean;
}) {
  const { t } = useLanguage();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputId = `ws-sites-${listKey}`;

  const messageFor = (code: SiteError, entry: string, site?: string) =>
    t(`bots.modal.webSearchSites.errors.${code}`, {
      value: entry, site: site || entry, max: MAX_SITES_PER_LIST,
    });

  const commit = (raw: string) => {
    // A paste can carry several sites; each is added or refused on its own.
    const entries = raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
    if (entries.length === 0) return;
    let next = value;
    let firstError: string | null = null;
    const refused: string[] = [];
    for (const entry of entries) {
      const result = addSite(next, listKey, entry);
      if ('error' in result) {
        firstError = firstError || messageFor(result.error, entry, result.site);
        refused.push(entry);
      } else {
        next = result.sites;
      }
    }
    if (next !== value) onChange(next);
    setError(firstError);
    setDraft(refused.join(' '));
  };

  const remove = (site: string) => {
    onChange({ ...value, [listKey]: value[listKey].filter(s => s !== site) });
    setError(null);
  };

  return (
    <div className="ws-sites__list" data-testid={`web-search-sites-${listKey}`}>
      <label className="ws-sites__label" htmlFor={inputId}>{label}</label>
      <p className="ws-sites__help">{help}</p>
      {value[listKey].length > 0 && (
        <ul className="ws-sites__chips">
          {value[listKey].map(site => (
            <li key={site} className="ws-sites__chip">
              <span className="ws-sites__chip-text" dir="ltr">{site}</span>
              <button
                type="button"
                className="ws-sites__chip-remove"
                onClick={() => remove(site)}
                disabled={disabled}
                aria-label={t('bots.modal.webSearchSites.remove', { site })}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="ws-sites__entry">
        <input
          id={inputId}
          className="ws-sites__input"
          type="text"
          dir="ltr"
          value={draft}
          placeholder={t('bots.modal.webSearchSites.placeholder')}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          onChange={e => { setDraft(e.target.value); if (error) setError(null); }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              commit(draft);
            }
          }}
          onPaste={e => {
            const text = e.clipboardData.getData('text');
            if (/[\s,;]/.test(text.trim())) {
              e.preventDefault();
              commit(text);
            }
          }}
        />
        <button
          type="button"
          className="ws-sites__add"
          onClick={() => commit(draft)}
          disabled={disabled || !draft.trim()}
        >
          {t('bots.modal.webSearchSites.add')}
        </button>
      </div>
      {error && (
        <p id={`${inputId}-error`} className="ws-sites__error" role="alert">{error}</p>
      )}
    </div>
  );
}

export default WebSearchSitesEditor;
